import { useEffect, useState, useMemo } from "react";
import Card from "./components/ui/Card";
import BaseTemplate from "./templates/BaseTemplate";
import {
  getInterpretationThemes,
  getNormativeProfiles,
} from "./api";
import { useNavigate } from "react-router";
import Button from "./components/ui/Button";
import { Plus, Activity, Percent, Scale, AlertTriangle, Users, Calendar, ChevronDown, Check, Settings } from "lucide-react";
import Badge from "./components/ui/Badge";
import { getUniqueBrainAreasFromChannels } from "./lib/utils";
import BadgeBrainRegion from "./components/ui/BadgeBrainRegion";

// ========== NORMATIVE PROFILES COMPONENTS ==========

// Clinical band order based on QEEG analysis reference
const BAND_ORDER = ["delta", "theta", "alpha", "smr", "beta", "beta2", "gamma"];

const RATIO_ORDER = [
  "delta_theta", "theta_alpha", "alpha_smr", "smr_beta1", "beta1_beta2",
  "theta_beta1", "theta_smr", "alpha_theta", "smr_beta2", "alpha_beta1", "beta1_alpha",
];

const sortByBandOrder = (entries) => {
  return [...entries].sort((a, b) => {
    const indexA = BAND_ORDER.indexOf(a[0].toLowerCase());
    const indexB = BAND_ORDER.indexOf(b[0].toLowerCase());
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
};

const sortByRatioOrder = (entries) => {
  return [...entries].sort((a, b) => {
    const indexA = RATIO_ORDER.indexOf(a[0]);
    const indexB = RATIO_ORDER.indexOf(b[0]);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
};

const formatBandName = (band) => {
  const bandNames = {
    delta: "Delta (δ)", theta: "Theta (θ)", alpha: "Alpha (α)",
    beta: "Beta (β)", beta2: "Beta2 (β2)", smr: "SMR", gamma: "Gamma (γ)",
  };
  return bandNames[band?.toLowerCase()] || band;
};

const DATA_CATEGORIES = [
  { id: "amplitude", label: "Amplitude Norms", icon: Activity, color: "text-blue-500", dataKey: "amplitude_uv" },
  { id: "relative_power", label: "Relative Power", icon: Percent, color: "text-green-500", dataKey: "relative_power" },
  { id: "ratios", label: "Ratios", icon: Scale, color: "text-purple-500", dataKey: "ratios" },
  { id: "thresholds", label: "Deviation Thresholds", icon: AlertTriangle, color: "text-amber-500", dataKey: "deviation_thresholds" },
];

const AmplitudeNormsTable = ({ amplitudeUv }) => {
  if (!amplitudeUv || Object.keys(amplitudeUv).length === 0) {
    return <p className="text-gray-400 text-sm italic">No amplitude norms defined</p>;
  }
  const entries = sortByBandOrder(Object.entries(amplitudeUv));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left py-2 px-3 font-medium text-gray-600">Band</th>
            <th className="text-right py-2 px-3 font-medium text-gray-600">Min</th>
            <th className="text-right py-2 px-3 font-medium text-gray-600">Max</th>
            <th className="text-left py-2 px-3 font-medium text-gray-600">Direction</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([band, value]) => {
            const isObject = typeof value === "object" && value !== null;
            return (
              <tr key={band} className="border-b border-gray-100 hover:bg-blue-50/50 transition-colors">
                <td className="py-2 px-3 font-medium text-gray-800">{formatBandName(band)}</td>
                <td className="py-2 px-3 text-right text-gray-500">{isObject ? (value.min ?? "—") : "—"}</td>
                <td className="py-2 px-3 text-right text-gray-500">{isObject ? (value.max ?? "—") : "—"}</td>
                <td className="py-2 px-3 text-gray-500 capitalize">{isObject ? (value.direction ?? "bidirectional") : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RelativePowerTable = ({ relativePower }) => {
  if (!relativePower || Object.keys(relativePower).length === 0) {
    return <p className="text-gray-400 text-sm italic">No relative power norms defined</p>;
  }
  const entries = sortByBandOrder(Object.entries(relativePower));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left py-2 px-3 font-medium text-gray-600">Band</th>
            <th className="text-right py-2 px-3 font-medium text-gray-600">Value (%)</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([band, value]) => (
            <tr key={band} className="border-b border-gray-100 hover:bg-green-50/50 transition-colors">
              <td className="py-2 px-3 font-medium text-gray-800">{formatBandName(band)}</td>
              <td className="py-2 px-3 text-right text-gray-600">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RatiosSection = ({ ratios }) => {
  if (!ratios || Object.keys(ratios).length === 0) {
    return <p className="text-gray-400 text-sm italic">No ratio configurations defined</p>;
  }
  const sortedRatios = sortByRatioOrder(Object.entries(ratios));
  return (
    <div className="grid gap-2">
      {sortedRatios.map(([ratioKey, config]) => (
        <div key={ratioKey} className="bg-gray-50 rounded-lg p-3 hover:bg-purple-50/50 transition-colors">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-gray-800 text-sm">{config.label || ratioKey}</span>
            <Badge variant="blue" className="text-xs">{config.numerator_band} / {config.denominator_band}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {config.min !== undefined && config.min !== null && (
              <div><span className="text-gray-500">Min:</span> <span className="text-gray-700">{config.min}</span></div>
            )}
            {config.max !== undefined && config.max !== null && (
              <div><span className="text-gray-500">Max:</span> <span className="text-gray-700">{config.max}</span></div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const DeviationThresholdsTable = ({ thresholds }) => {
  if (!thresholds || Object.keys(thresholds).length === 0) {
    return <p className="text-gray-400 text-sm italic">No deviation thresholds defined</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left py-2 px-3 font-medium text-gray-600">Threshold Type</th>
            <th className="text-right py-2 px-3 font-medium text-gray-600">Value</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(thresholds).map(([key, value]) => (
            <tr key={key} className="border-b border-gray-100 hover:bg-amber-50/50 transition-colors">
              <td className="py-2 px-3 text-gray-800 capitalize">{key.replace(/_/g, " ")}</td>
              <td className="py-2 px-3 text-right text-gray-600">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProfileSelector = ({ profiles, selectedProfile, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-left bg-white border border-gray-200 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{selectedProfile?.name || "Select a profile"}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => { onSelect(profile); setIsOpen(false); }}
                className={`w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors flex items-center justify-between cursor-pointer ${selectedProfile?.id === profile.id ? 'bg-blue-50' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{profile.name}</p>
                </div>
                {selectedProfile?.id === profile.id && <Check className="w-4 h-4 text-blue-500 ml-2 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CategoryTab = ({ category, isActive, count, onClick }) => {
  const Icon = category.icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all w-full cursor-pointer ${
        isActive ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-600 hover:bg-gray-50 border border-transparent'
      }`}
    >
      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-blue-500' : category.color}`} />
      <span className="flex-1 text-left">{category.label}</span>
      {count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-xs ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
      )}
    </button>
  );
};

const DataPanel = ({ category, data }) => {
  const Icon = category.icon;
  const renderContent = () => {
    switch (category.id) {
      case "amplitude": return <AmplitudeNormsTable amplitudeUv={data?.amplitude_uv} />;
      case "relative_power": return <RelativePowerTable relativePower={data?.relative_power} />;
      case "ratios": return <RatiosSection ratios={data?.ratios} />;
      case "thresholds": return <DeviationThresholdsTable thresholds={data?.deviation_thresholds} />;
      default: return null;
    }
  };
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 mb-3">
        <Icon className={`w-4 h-4 ${category.color}`} />
        <h3 className="text-sm font-semibold text-gray-800">{category.label}</h3>
      </div>
      <div className="flex-1 overflow-auto">{renderContent()}</div>
    </div>
  );
};

// ========== MAIN COMPONENT ==========

const ConfigurationCenter = () => {
  const navigate = useNavigate();
  
  // Interpretation Themes state
  const [interpretationThemes, setInterpretationThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);
  
  // Normative Profiles state
  const [normativeProfiles, setNormativeProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [activeCategory, setActiveCategory] = useState(DATA_CATEGORIES[0]);
  const [profilesLoading, setProfilesLoading] = useState(true);

  const fetchInterpretationThemes = async () => {
    setThemesLoading(true);
    try {
      const response = await getInterpretationThemes();
      setInterpretationThemes(response.data || []);
    } catch (err) {
      console.error("Error fetching interpretation themes:", err);
      setInterpretationThemes([]);
    } finally {
      setThemesLoading(false);
    }
  };

  const fetchNormativeProfiles = async () => {
    setProfilesLoading(true);
    try {
      const response = await getNormativeProfiles();
      const profiles = response.data || [];
      setNormativeProfiles(profiles);
      if (profiles.length > 0) {
        const defaultProfile = profiles.find(p => 
          p.description?.toLowerCase().includes("standard normative values for adults")
        ) || profiles[0];
        setSelectedProfile(defaultProfile);
      }
    } catch (err) {
      console.error("Error fetching normative profiles:", err);
      setNormativeProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    fetchInterpretationThemes();
    fetchNormativeProfiles();
  }, []);

  const categoryCounts = useMemo(() => {
    if (!selectedProfile?.data) return {};
    return {
      amplitude: selectedProfile.data.amplitude_uv ? Object.keys(selectedProfile.data.amplitude_uv).length : 0,
      relative_power: selectedProfile.data.relative_power ? Object.keys(selectedProfile.data.relative_power).length : 0,
      ratios: selectedProfile.data.ratios ? Object.keys(selectedProfile.data.ratios).length : 0,
      thresholds: selectedProfile.data.deviation_thresholds ? Object.keys(selectedProfile.data.deviation_thresholds).length : 0,
    };
  }, [selectedProfile]);

  // Helper to format bands for display
  const formatBands = (bands) => {
    if (!bands || bands.length === 0 || (bands.length === 1 && bands[0] === "")) {
      return <span className="text-gray-400 italic">All</span>;
    }
    return bands.slice(0, 3).map((band, idx) => (
      <Badge key={idx} variant="default" className="text-xs capitalize">
        {band}
      </Badge>
    ));
  };

  return (
    <BaseTemplate>
      <div className="flex gap-6 h-[calc(100vh-140px)] min-h-[600px]">
        {/* LEFT PANEL - Interpretation Themes (65%) */}
        <div className="w-[65%] flex flex-col">
          <Card name="Interpretation Themes" className="flex-1 flex flex-col overflow-hidden">
            <div className="flex justify-end mb-3">
              <Button
                onClick={() => navigate("/interpretation-themes/add")}
                className="text-white px-3 py-1.5 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Theme
              </Button>
            </div>
            
            {themesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-500 text-sm">Loading themes...</span>
              </div>
            ) : interpretationThemes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-gray-500">No interpretation themes found</p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200">
                      <th className="p-2 text-left font-medium text-gray-600 w-[40%]">Description</th>
                      <th className="p-2 text-left font-medium text-gray-600">Source</th>
                      <th className="p-2 text-left font-medium text-gray-600">Bands</th>
                      <th className="p-2 text-left font-medium text-gray-600">Keywords</th>
                      <th className="p-2 text-left font-medium text-gray-600">Regions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interpretationThemes.map((theme, index) => (
                      <tr
                        key={theme.id}
                        className="border-b border-gray-100 even:bg-gray-50 hover:bg-blue-50/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/interpretation-themes/themes/${theme.id}`)}
                      >
                        <td className="p-2">
                          <span className="line-clamp-2 text-gray-800">{theme.interpretation_text}</span>
                        </td>
                        <td className="p-2">
                          <span className="text-gray-600 text-xs">{theme.source_type}</span>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {formatBands(theme.theme_involved_bands)}
                            {theme.theme_involved_bands && theme.theme_involved_bands.length > 3 && (
                              <span className="text-xs text-gray-500">+{theme.theme_involved_bands.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          {theme.keywords && theme.keywords.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {theme.keywords.slice(0, 2).map((keyword, idx) => {
                                if (keyword !== "") {
                                  return (
                                    <Badge key={idx} variant="white" className="text-xs">
                                      {keyword}
                                    </Badge>
                                  );
                                }
                                return null;
                              })}
                              {theme.keywords.filter(k => k !== "").length > 2 && (
                                <span className="text-xs text-gray-500">+{theme.keywords.filter(k => k !== "").length - 2}</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {getUniqueBrainAreasFromChannels(theme.theme_involved_channels ?? []).slice(0, 2).map((area, idx) => (
                              <BadgeBrainRegion key={idx} region={area} />
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT PANEL - Normative Profiles (35%) */}
        <div className="w-[35%] flex flex-col">
          <Card name="Normative Profiles" className="flex-1 flex flex-col overflow-hidden">
            {profilesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-500 text-sm">Loading profiles...</span>
              </div>
            ) : normativeProfiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Settings className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">No normative profiles found</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4 h-full">
                {/* Profile Selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                    Select Profile
                  </label>
                  <ProfileSelector
                    profiles={normativeProfiles}
                    selectedProfile={selectedProfile}
                    onSelect={setSelectedProfile}
                  />
                </div>

                {/* Profile Metadata */}
                {selectedProfile && (
                  <div className="flex flex-wrap gap-2">
                    {selectedProfile.target_age_range && (
                      <Badge variant="blue" className="flex items-center gap-1 text-xs">
                        <Calendar className="w-3 h-3" />
                        {selectedProfile.target_age_range}
                      </Badge>
                    )}
                    {selectedProfile.target_gender && (
                      <Badge variant="violet" className="flex items-center gap-1 text-xs">
                        <Users className="w-3 h-3" />
                        {selectedProfile.target_gender}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Category Tabs */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                    Data Categories
                  </label>
                  <div className="space-y-1">
                    {DATA_CATEGORIES.map((category) => (
                      <CategoryTab
                        key={category.id}
                        category={category}
                        isActive={activeCategory.id === category.id}
                        count={categoryCounts[category.id] || 0}
                        onClick={() => setActiveCategory(category)}
                      />
                    ))}
                  </div>
                </div>

                {/* Data Panel */}
                <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 overflow-hidden min-h-0">
                  {selectedProfile ? (
                    <DataPanel category={activeCategory} data={selectedProfile.data} />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                      <Settings className="w-8 h-8 mb-2" />
                      <p className="text-sm">Select a profile</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </BaseTemplate>
  );
};

export default ConfigurationCenter;
